import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../../components/shared/Button/Button";
import { SpinnerLoading } from "../../components/shared/Spinner/SpinnerLoading";
import {
  LANDING_PAGE_URL,
  UNIVERSAL_LOGIN_PAGE_URL,
} from "../../utils/urlConstants";
import { useCreateProviderSession } from "../../utils/hooks/useCreateProviderSession";
import {
  ProviderAuthActions,
  ProviderAuthShell,
  ProviderAuthSpinnerWrapper,
} from "../../auth/ProviderAuthShell";

type AuthCallbackScreenState = "authenticating" | "error";

const AuthCallback = (): React.ReactElement => {
  const history = useHistory();
  const { error, getAccessTokenSilently, isAuthenticated, isLoading } =
    useAuth0();
  const [screenState, setScreenState] =
    useState<AuthCallbackScreenState>("authenticating");
  const [errorMessage, setErrorMessage] = useState("");

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
    setScreenState("error");
  }, []);

  const { isProviderSessionReady } = useCreateProviderSession({
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    onError: showError,
  });

  const redirectToPostLoginPath = useCallback(() => {
    const nextPath =
      sessionStorage.getItem("current-pathname") || LANDING_PAGE_URL;
    sessionStorage.removeItem("current-pathname");
    history.replace(nextPath);
  }, [history]);

  useEffect(() => {
    if (isProviderSessionReady) {
      redirectToPostLoginPath();
    }
  }, [isProviderSessionReady, redirectToPostLoginPath]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (error || !isAuthenticated) {
      showError("Please try again.");
    }
  }, [error, isAuthenticated, isLoading, showError]);

  return (
    <ProviderAuthShell
      description={screenState === "authenticating" ? undefined : errorMessage}
      isError={screenState === "error"}
      title={
        screenState === "authenticating"
          ? "Signing you in"
          : "Unable to sign in"
      }
    >
      {screenState === "authenticating" ? (
        <ProviderAuthSpinnerWrapper>
          <SpinnerLoading />
        </ProviderAuthSpinnerWrapper>
      ) : (
        <ProviderAuthActions>
          <Button
            label="Continue to sign in"
            onClick={() => history.replace(UNIVERSAL_LOGIN_PAGE_URL)}
            size="medium"
            textColor="white"
          />
        </ProviderAuthActions>
      )}
    </ProviderAuthShell>
  );
};

export default AuthCallback;
